import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { convertCsvToExportFormat } from '../../../utils/exportUtils';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function HousekeepingReportTab({ tenantId, isActive }) {
    const [hkData, setHkData] = useState([]);
    const [hkSummary, setHkSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        period: 'daily',
        staffId: '',
        taskTypeId: '',
        floorId: ''
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchHkData();
        }
    }, [isActive, tenantId, filters, page, rowsPerPage]);

    const fetchHkData = async (isExport = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { ...filters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = page + 1;
                params.limit = rowsPerPage;
            }
            
            const res = await ReportService.getHousekeepingReport(tenantId, params);
            const reportData = res?.data || res;
            setHkSummary(reportData || null);
            setHkData(reportData?.tasks || reportData?.items || reportData?.housekeeping || []);
        } catch (error) {
            console.error('Error fetching housekeeping report:', error);
            setError('Failed to load housekeeping report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            period: 'daily',
            staffId: '',
            taskTypeId: '',
            floorId: ''
        });
        setPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setError(null);
        try {
            const params = { ...filters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportHousekeepingReport(tenantId, params);
            
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            const fileNameBase = `Housekeeping_Report_${new Date().toISOString().split('T')[0]}`;
            await convertCsvToExportFormat(csvText, format, fileNameBase);
        } catch (err) {
            console.error(`Error exporting housekeeping report as ${format}:`, err);
            setError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.period}
                                displayEmpty
                                onChange={(e) => handleFilterChange('period', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.staffId}
                                displayEmpty
                                onChange={(e) => handleFilterChange('staffId', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Staff</MenuItem>
                                {/* Populate dynamically if API provides list */}
                                <MenuItem value="stf-1">Maria G.</MenuItem>
                                <MenuItem value="stf-2">John D.</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.taskTypeId}
                                displayEmpty
                                onChange={(e) => handleFilterChange('taskTypeId', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Tasks</MenuItem>
                                <MenuItem value="Deep Clean">Deep Clean</MenuItem>
                                <MenuItem value="Stayover Service">Stayover Service</MenuItem>
                                <MenuItem value="Check-out Turnover">Check-out Turnover</MenuItem>
                                <MenuItem value="Touch Up">Touch Up</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.floorId}
                                displayEmpty
                                onChange={(e) => handleFilterChange('floorId', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Floors</MenuItem>
                                <MenuItem value="floor-1">1st Floor</MenuItem>
                                <MenuItem value="floor-2">2nd Floor</MenuItem>
                                <MenuItem value="floor-3">3rd Floor</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(filters.startDate !== '' || filters.endDate !== '' || filters.period !== 'daily' || filters.staffId !== '' || filters.taskTypeId !== '' || filters.floorId !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {hkSummary && !isLoading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{hkSummary.completedCount || 0} / {hkSummary.totalTasks || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Tasks Completed</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{hkSummary.overdueCount || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Overdue Tasks</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{hkSummary.slaCompliancePercent !== undefined ? Number(hkSummary.slaCompliancePercent).toFixed(2) : '0.00'}%</Typography>
                            <Typography variant="body2" color="text.secondary">SLA Compliance</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Room ID</TableCell>
                            <TableCell>Staff Member</TableCell>
                            <TableCell>Task Type / Priority</TableCell>
                            <TableCell>Due By</TableCell>
                            <TableCell>Completed At</TableCell>
                            <TableCell align="right">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            hkData.length > 0 ? (
                                hkData.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{row.roomId?.substring(0, 8) || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>{row.assignedStaff?.fullName || 'Unassigned'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.taskType?.name || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.priority ? `Priority: ${row.priority}` : '-'}</Typography>
                                        </TableCell>
                                        <TableCell>{row.dueBy ? new Date(row.dueBy).toLocaleString() : '-'}</TableCell>
                                        <TableCell>{row.completedAt ? new Date(row.completedAt).toLocaleString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={row.status || 'Unknown'} 
                                                size="small" 
                                                color={
                                                    (row.status || '').toLowerCase() === 'completed' ? 'success' :
                                                    (row.status || '').toLowerCase() === 'cancelled' ? 'error' :
                                                    (row.status || '').toLowerCase() === 'in progress' ? 'info' :
                                                    'warning'
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No housekeeping records found for the selected criteria.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={hkData.length >= rowsPerPage ? -1 : hkData.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
